<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Daily Order Estimate Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyOrderDurationFilter"
              @reset="resetOrderDurationFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingOrderDurationFilter.startDate"
                  :endDate="editingOrderDurationFilter.endDate"
                  @updateStartDate="editingOrderDurationFilter.startDate = $event"
                  @updateEndDate="editingOrderDurationFilter.endDate = $event"
                />
                <FormHorizontalRule />
                Ignore Requested Date Orders
                <FormSwitch v-model="editingOrderDurationFilter.ignoreRequestedDateSet" booleanMode />
                <FormHorizontalRule />
                Order Type
                <FormMultiSelect :options="orderFilterTypeOptions" v-model="editingOrderDurationFilter.orderTypeList" />
                <FormHorizontalRule />
                Despatch Type
                <FormMultiSelect :options="despatchFilterTypeOptions" v-model="editingOrderDurationFilter.despatchTaskTypeIdList" />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedOrderDurationFilter.startDate | formatDate}} - {{appliedOrderDurationFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Production + Despatch Time">
              {{formatDecimal(orderSummaryData.averageProductionTime + orderSummaryData.averageDespatchTime, 2)}} hrs
              <template v-slot:info>Time orders spent during production and despatch during business hours over {{formatInteger(orderSummaryData.totalOrderCount, false, true)}} order{{displayCountPlural(orderSummaryData.totalOrderCount)}} completed during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Estimated Time">
              {{formatDecimal(orderSummaryData.averageEstimatedTime, 2)}} hrs
              <template v-slot:info>Time orders were estimated to take during business hours over {{formatInteger(orderSummaryData.totalOrderCount, false, true)}} order{{displayCountPlural(orderSummaryData.totalOrderCount)}} completed during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Order Estimate"
          :series="orderDailyDurationData.series"
          :xAxis="orderDailyDurationData.xAxis"
          :yAxis="orderDailyDurationData.yAxis"
          :legend="orderDailyDurationData.legend"
          :options="orderDailyDurationData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

import { OrderTypes, OrderTypeLookup } from '@/store/types'

export default {
  name: 'OrderEstimateDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      orderSummaryData: {
        totalOrderCount: 0,
        totalOrderTime: 0,
        totalEstimatedTime: 0,
        averageOrderTime: 0,
        averageEstimatedTime: 0,

        totalProductionTime: 0,
        totalDespatchTime: 0,
        totalOtherTime: 0,
        averageProductionTime: 0,
        averageDespatchTime: 0,
        averageOtherTime: 0,
      },
      orderDailyDurationData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultOrderDurationFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        orderTypeList: [OrderTypes.order, OrderTypes.nonChargeableRework],
        despatchTaskTypeIdList: [],
        ignoreRequestedDateSet: false
      },
      taskTypeList: [],
      editingOrderDurationFilter: {},
      appliedOrderDurationFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
    orderFilterTypeOptions() {
      // Ignore quotes
      return OrderTypeLookup.filter(lookup => lookup.value == OrderTypes.order || lookup.value == OrderTypes.nonChargeableRework)
    },
    despatchFilterTypeOptions() {
      return this.taskTypeList.map(taskType => {
        return {
          value: taskType.id,
          name: taskType.name
        }
      })
    }
  },
  mounted() {
    this.editingOrderDurationFilter = _.clone(this.defaultOrderDurationFilter)
    this.appliedOrderDurationFilter = _.clone(this.defaultOrderDurationFilter)

    this.requestData()
    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyOrderDurationFilter() {
      this.appliedOrderDurationFilter = _.clone(this.editingOrderDurationFilter)

      this.requestChartData()
    },
    resetOrderDurationFilter() {
      this.editingOrderDurationFilter = _.clone(this.defaultOrderDurationFilter)
      this.appliedOrderDurationFilter = _.clone(this.defaultOrderDurationFilter)

      this.requestChartData()
    },
    requestData() {
      this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1 }})
      .then(response => {
        this.taskTypeList = response.data

        this.defaultOrderDurationFilter.despatchTaskTypeIdList = this.taskTypeList.map(taskType => taskType.id)
        this.resetOrderDurationFilter()
      })
      .catch(err => {
        console.error(err)
      })
    },
    requestChartData() {
      var orderDurationFilter = {
        type: 'daily',
        startDate: this.appliedOrderDurationFilter.startDate,
        endDate: this.appliedOrderDurationFilter.endDate,
        contactId: this.contactId,
        ignoreRequestedDateSet: this.appliedOrderDurationFilter.ignoreRequestedDateSet
      }
      if(this.appliedOrderDurationFilter.orderTypeList && this.appliedOrderDurationFilter.orderTypeList.length > 0)
        orderDurationFilter.orderTypeList = this.appliedOrderDurationFilter.orderTypeList
      if(this.appliedOrderDurationFilter.despatchTaskTypeIdList && this.appliedOrderDurationFilter.despatchTaskTypeIdList.length > 0)
        orderDurationFilter.despatchTaskTypeIdList = this.appliedOrderDurationFilter.despatchTaskTypeIdList

      let widgetComp = this

      this.$apiService.analyticsService.getOrdersDurationChartData(orderDurationFilter)
      .then(response => {
        let chartData = response.data

        this.orderDailyDurationData.legend = {
          data: ['Avg Production Time', 'Avg Despatch Time', 'Avg Estimated Time', 'Completed Order Count']
        }
        this.orderDailyDurationData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
        }
        this.orderDailyDurationData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return `${value} hrs`;
              }
            }
          },
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return `${value} hrs`;
              }
            }
          },
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return `${value} hrs`;
              }
            }
          },
          { type: 'value', alignTicks: true }
        ]
        this.orderDailyDurationData.series = []
        this.orderDailyDurationData.series.push({
          type: 'bar',
          name: 'Avg Production Time',
          stack: 'time',
          data: chartData.map(chartEntry => chartEntry.totalOrderCount > 0 ? (chartEntry.totalProductionDuration / 60.0) / chartEntry.totalOrderCount : 0),
          emphasis: {
            focus: 'series'
          },
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatDecimal(value, 2) + ' hrs';
            }
          }
        })
        this.orderDailyDurationData.series.push({
          type: 'bar',
          name: 'Avg Despatch Time',
          stack: 'time',
          data: chartData.map(chartEntry => chartEntry.totalOrderCount > 0 ? (chartEntry.totalDespatchDuration / 60.0) / chartEntry.totalOrderCount : 0),
          emphasis: {
            focus: 'series'
          },
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatDecimal(value, 2) + ' hrs';
            }
          }
        })
        this.orderDailyDurationData.series.push({
          type: 'bar',
          name: 'Avg Estimated Time',
          data: chartData.map(chartEntry => chartEntry.totalOrderCount > 0 ? (chartEntry.totalEstimatedDuration / 60.0) / chartEntry.totalOrderCount : 0),
          emphasis: {
            focus: 'series'
          },
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatDecimal(value, 2) + ' hrs';
            }
          }
        })

        this.orderDailyDurationData.series.push({
          type: 'line',
          name: 'Completed Order Count',
          yAxisIndex: 3,
          data: chartData.map(chartEntry => chartEntry.totalOrderCount),
        })

        this.orderSummaryData.totalOrderCount = chartData.reduce((cur, chartEntry) => cur + chartEntry.totalOrderCount, 0)
        this.orderSummaryData.totalOrderTime = chartData.reduce((cur, chartEntry) => cur + chartEntry.totalOrderDuration, 0) / 60.0
        this.orderSummaryData.totalEstimatedTime = chartData.reduce((cur, chartEntry) => cur + chartEntry.totalEstimatedDuration, 0) / 60.0

        this.orderSummaryData.totalProductionTime = chartData.reduce((cur, chartEntry) => cur + chartEntry.totalProductionDuration, 0) / 60.0
        this.orderSummaryData.totalDespatchTime = chartData.reduce((cur, chartEntry) => cur + chartEntry.totalDespatchDuration, 0) / 60.0
        this.orderSummaryData.totalOtherTime = Math.max(this.orderSummaryData.totalOrderTime - this.orderSummaryData.totalProductionTime - this.orderSummaryData.totalDespatchTime, 0)

        this.orderSummaryData.averageProductionTime = 0
        this.orderSummaryData.averageDespatchTime = 0
        this.orderSummaryData.averageOtherTime = 0
        this.orderSummaryData.averageOrderTime = 0
        this.orderSummaryData.averageEstimatedTime = 0

        if(this.orderSummaryData.totalOrderCount > 0) {
          this.orderSummaryData.averageProductionTime = this.orderSummaryData.totalProductionTime / this.orderSummaryData.totalOrderCount
          this.orderSummaryData.averageDespatchTime = this.orderSummaryData.totalDespatchTime / this.orderSummaryData.totalOrderCount
          this.orderSummaryData.averageOtherTime = this.orderSummaryData.totalOtherTime / this.orderSummaryData.totalOrderCount

          this.orderSummaryData.averageOrderTime = this.orderSummaryData.totalOrderTime / this.orderSummaryData.totalOrderCount
          this.orderSummaryData.averageEstimatedTime = this.orderSummaryData.totalEstimatedTime / this.orderSummaryData.totalOrderCount
        }
      })
    }
  }
}
</script>